import { useEffect, useState } from 'react'

import { restClient } from '@/Core/Services/RestClientService'

/** Пов'язаний запис довідника в картці: лише поля, потрібні плитці */
export interface RelatedRecord {
    code: string
    title: string
    isActive: boolean
    externalCode?: string | null
}

export type RelatedRecordsState =
    | { status: 'loading' }
    | { status: 'done'; items: RelatedRecord[]; total: number }
    | { status: 'error' }

/** Максимальний `limit` колекції на бекенді; якщо записів більше — секція покаже лічильник */
export const RELATED_RECORDS_LIMIT = 300

interface RecordsCollection {
    items?: RelatedRecord[]
    meta?: { total?: number }
}

/**
 * Фільтр колекції за зв'язком з поточним записом: `<relation>.code = code`, без видалених
 * (м'яко видалені бекенд у колекціях сам не відкидає).
 */
export const buildRelationFilters = (relation: string, code: string) => ({
    and: [
        { field: `${relation}.code`, op: '=', value: code },
        { field: 'isRemoved', op: '=', value: false },
    ],
})

/**
 * Записи довідника `restUrl`, пов'язані з поточним записом через поле `relation`
 * (напр. регіони кластера: `/services/lists/regions`, `cluster`), у порядку сортування
 * довідника. Помилку запиту перетворює на стан error.
 */
export const fetchRelatedRecords = async (
    restUrl: string,
    relation: string,
    code: string
): Promise<RelatedRecordsState> => {
    try {
        // RestClient без isOriginalResponse резолвить уже розібраний JSON, а не Response
        const data = (await restClient.get(
            restUrl,
            {
                searchParams: {
                    limit: RELATED_RECORDS_LIMIT,
                    filters: JSON.stringify(buildRelationFilters(relation, code)),
                },
            },
            { forceRefresh: true }
        )) as RecordsCollection | null
        const items = data?.items ?? []
        return { status: 'done', items, total: data?.meta?.total ?? items.length }
    } catch {
        return { status: 'error' }
    }
}

/** Пов'язані записи для картки перегляду. Без коду (картка ще вантажиться) — loading. */
export const useRelatedRecords = (restUrl: string, relation: string, code: string | undefined): RelatedRecordsState => {
    const [state, setState] = useState<RelatedRecordsState>({ status: 'loading' })

    useEffect(() => {
        if (!code) return undefined
        let cancelled = false
        setState({ status: 'loading' })
        void fetchRelatedRecords(restUrl, relation, code).then((next) => {
            if (!cancelled) setState(next)
        })
        return () => {
            cancelled = true
        }
    }, [restUrl, relation, code])

    return state
}
